/**
 * Where this console is mounted, and how it builds its own URLs.
 *
 * ## The problem
 *
 * Every console deployed today owns a whole host — `hq.ledai.build`,
 * `app.basereality.ai` — so this resolves to `""` and is the identity
 * function. It exists for the path-mounted case (see `server/mountPath.ts`,
 * which explains why that is currently unused): under `example.com/app`, an
 * unmounted `/api/items` reaches whatever else lives at that domain's root
 * and returns ITS index page with a 200 — not an error, just the wrong
 * document, which is the worst failure shape available.
 *
 * ## Derived, not configured
 *
 * The prefix comes from `location.pathname` rather than from a build flag or
 * an env var. That is safe here for a specific reason: the console routes
 * entirely by QUERY PARAMETER (`?page=brief&item=…` — see `Shell.tsx`'s
 * `hrefWith`), never by path segment. Its pathname is therefore constant for
 * the life of the document, and the path it was served at IS its mount point.
 *
 * The alternative — baking the base path in at build time — would mean the
 * same image could not be served at both a host root and a path, and a
 * mismatch between the built value and the deployed location would surface
 * as 200-with-wrong-content rather than as anything obviously broken.
 *
 * Links need no help: `hrefWith` emits `?query` strings, which the browser
 * already resolves against the current path.
 */

/** The mount prefix implied by a served path. `""` at a host root.
 *
 *  PAGE paths are stripped first: `/home` is the console's own route (the
 *  Home page's clean URL), not part of the mount — served at
 *  `example.com/app/home`, the mount is `/app`. Every page path the console
 *  ever grows must be listed here, or a reload on it corrupts BASE_PATH and
 *  every API call follows it.
 *
 *  `/sign-up` is the second one: the console's two unauthenticated screens
 *  are pages rather than query states, because Clerk navigates BETWEEN them
 *  by URL and a visitor who reloads on one must land back on it. */
const PAGE_PATHS = /\/(home|sign-up)\/?$/;

export function basePathFrom(pathname: string): string {
  return pathname.replace(PAGE_PATHS, "").replace(/\/+$/, "");
}

/** `path` under `base`, with no doubled separator. Absolute URLs pass through
 *  untouched — a helper that rewrote `https://…` would break in a way that
 *  reads as a network fault rather than as a bug here. */
export function joinBase(base: string, path: string): string {
  if (/^[a-z][a-z0-9+.-]*:/i.test(path)) return path;
  const prefix = base.replace(/\/+$/, "");
  if (prefix === "") return path;
  return `${prefix}${path.startsWith("/") ? "" : "/"}${path}`;
}

/**
 * This document's mount point, resolved once.
 *
 * Read at module load: the console never changes its pathname, so re-reading
 * it per request would cost the same answer every time. Guarded for
 * non-browser contexts (the unit suite imports this module under happy-dom,
 * and `location` is absent in a bare Bun test).
 */
export const BASE_PATH: string =
  typeof location === "undefined" ? "" : basePathFrom(location.pathname);

/** A console URL, mounted. Every request the console makes goes through this. */
export function consoleUrl(path: string): string {
  return joinBase(BASE_PATH, path);
}

/** Deployment mount is independent of the selected company. */
export let MOUNT_PATH = "";
export function setMountPath(path: string): void {
  MOUNT_PATH = path.replace(/\/+$/, "");
}
